import _ from "underscore";
import { IHosProcessResult } from "../../../services/hosProcess";
import {
    convertMsToString,
    isOFFDutyStatus,
    getAvailabilityDisplaySummary,
    getDisplayObjectHos,
    DISPLAY_CHECKMARK,
    type IAvailabilityDisplayInfo,
    timeSpanToMs,
    getRuleSetById,
    type IHosRuleset
} from "hos-tools";
import DutyStatusLog from "../../../model/DutyStatusLog";
import HOSPageHTML from "./availabilityDisplay.HOS.tmpl";
import EWDPageHTML from "./availabilityDisplay.EWD.tmpl";
import { dstCalculator } from "../../../utils/date";
import { HOURS } from "../../../constants";

export default class AvailabilityDisplay {
    private readonly hosTemplate = _.template(HOSPageHTML);
    private readonly ewdTemplate = _.template(EWDPageHTML);
    private translatedHourUnit = "";
    private translatedMinuteUnit = "";
    private tooltips: Record<string, string> = {};

    setTooltips(tooltips: Record<string, string>): void {
        this.tooltips = tooltips;
    }

    setTranslatedStrings(translatedHourUnit = "h", translatedMinuteUnit = "m") {
        this.translatedHourUnit = translatedHourUnit;
        this.translatedMinuteUnit = translatedMinuteUnit;
    }

    getDisplay(
        ruleResult: IHosProcessResult,
        ruleSetKey: string,
        currentLog: DutyStatusLog | null,
        daysString: string,
        mode: "HOS" | "EWD" = "HOS",
        currentTime: Date | undefined = undefined,
        isVerboseDisplay = true,
        translatedHourUnit = "h",
        translatedMinuteUnit = "m",
        translatedDrivingBreakDuration = "{0} Break duration",
        translatedDrivingBreak = "{0} Break",
        translatedDrivingBreakIn = "{0} Break required in"
    ): string {
        this.setTranslatedStrings(translatedHourUnit, translatedMinuteUnit);

        if (mode === "HOS") {
            let hosDisplayObject = getDisplayObjectHos(
                ruleResult,
                ruleSetKey,
                currentLog,
                daysString,
                translatedHourUnit,
                translatedMinuteUnit,
                translatedDrivingBreakDuration,
                translatedDrivingBreak,
                translatedDrivingBreakIn
            );
                let availabilityDisplaySummary = getAvailabilityDisplaySummary(hosDisplayObject, ruleResult.preciseAvailability);
                let availabilityRows = this.mapAvailabilityDisplayInfoToHtmlTableRow(availabilityDisplaySummary);
                return this.hosTemplate({ availabilityRows });
        } else {
            // FIGURE OUT THE EWD AVAILABILITY??
            if (ruleResult.ewdAvailability && ruleResult.ewdAvailability.length > 0) {
                let timeUntilMustRestArray = ruleResult.ewdAvailability.map(ewdObject => ewdObject.timeUntilMustRest ? timeSpanToMs(ewdObject.timeUntilMustRest) : 0);
                let isResting = !currentLog || isOFFDutyStatus(currentLog.status);
                let firstTimePeriod = ruleResult.ewdAvailability[ruleResult.ewdAvailability.length - 1];
                let restDuration = isResting && firstTimePeriod && firstTimePeriod.rested ? timeSpanToMs(firstTimePeriod.rested) : undefined;
                let minTimeUntil15MinuteBreak = Math.min.apply(Math, [timeUntilMustRestArray[0], timeUntilMustRestArray[1], timeUntilMustRestArray[2]]);
                let ruleset = getRuleSetById(ruleSetKey);
                let restCheckmarks = this.getRestCheckmarksTemplate(ruleResult, ruleset || <IHosRuleset>{});
                let realTimeUntil15 = new Date((currentTime?.getTime() || 0) + minTimeUntil15MinuteBreak);
                let realTimeUntil7 = new Date((currentTime?.getTime() || 0) + timeUntilMustRestArray[3]);
                let realTimeUntil24 = new Date((currentTime?.getTime() || 0) + timeUntilMustRestArray[4]);
                let realTimeUntilNightRest = new Date((currentTime?.getTime() || 0) + timeUntilMustRestArray[5]);

                realTimeUntil15.setSeconds(0);
                realTimeUntil7.setSeconds(0);
                realTimeUntil24.setSeconds(0);
                realTimeUntilNightRest.setSeconds(0);

                let dayCycleStarted = dstCalculator.toLocalDateTime(ruleResult.ewdAvailability[3].startDate as Date);
                let sevenDayCycleStarted = dstCalculator.toLocalDateTime(ruleResult.ewdAvailability[4].startDate as Date);
                let fourteenDayCycleStarted = dstCalculator.toLocalDateTime(ruleResult.ewdAvailability[5].startDate as Date);

                dayCycleStarted.setSeconds(0);
                sevenDayCycleStarted.setSeconds(0);
                fourteenDayCycleStarted.setSeconds(0);

                return this.ewdTemplate({
                    isResting,
                    restDuration: restDuration !== undefined ? this.getTimeString(restDuration) : "",
                    realTimeUntil15: realTimeUntil15.toLocaleString("en-au", { hour12: false }),
                    realTimeUntil7: realTimeUntil7.toLocaleString("en-au", { hour12: false }),
                    realTimeUntil24: realTimeUntil24.toLocaleString("en-au", { hour12: false }),
                    realTimeUntilNightRest: realTimeUntilNightRest.toLocaleString("en-au", { hour12: false }),
                    timeUntil15: convertMsToString(minTimeUntil15MinuteBreak / HOURS, this.translatedHourUnit, this.translatedMinuteUnit),
                    timeUntil7: convertMsToString(timeUntilMustRestArray[3] / HOURS, this.translatedHourUnit, this.translatedMinuteUnit),
                    timeUntil24: convertMsToString(timeUntilMustRestArray[4] / HOURS, this.translatedHourUnit, this.translatedMinuteUnit),
                    timeUntilNightRest: convertMsToString(timeUntilMustRestArray[5] / HOURS, this.translatedHourUnit, this.translatedMinuteUnit),
                    DayCycleStarted: dayCycleStarted.toLocaleString("en-au", { hour12: false }),
                    SevenDayCycleStarted: sevenDayCycleStarted.toLocaleString("en-au", { hour12: false }),
                    FourteenDayCycleStarted: fourteenDayCycleStarted.toLocaleString("en-au", { hour12: false }),
                    oneNightRestChecks: restCheckmarks[0],
                    twoNightRestChecks: restCheckmarks[1],
                    isVerboseDisplay
                });
            }
            return "";
        }
    }

    getTimeString(ms: number) {
        let seconds = ms / 1000;
        let minutes = seconds / 60;
        let hours = minutes / 60;
        let days = hours / 24;
        let rMinutes = ~~(minutes % 60);
        let rHours = ~~(hours % 24);
        let rDays = ~~days;

        return `${ (rDays * 24) + rHours }${ this.translatedHourUnit } ${ rMinutes }${ this.translatedMinuteUnit }`;
    }

    private getRestCheckmarksTemplate(ruleResult: IHosProcessResult, ruleset: IHosRuleset): string[] {
        let templateStrings = ["", ""];

        if (ruleResult.ewdAvailability && ruleset.config.workRestConfig) {
            let oneNightRestChecks = ruleset.config.workRestConfig[5].restPeriod?.nightRestBreakCount || 0; // HARD CODED FOR STANDARD SOLO HOURS
            let twoNightRestChecks = ruleset.config.workRestConfig[5].restPeriod?.consecutiveNightRestBreakCount || 0; // HARD CODED FOR STANDARD SOLO HOURS

            let fulfilledOneNightRest = ruleResult.ewdAvailability[5].nightRestBreakCount || 0;
            let unfulfilledOneNightRest = oneNightRestChecks - fulfilledOneNightRest;
            for (let i = 0; i < fulfilledOneNightRest; i++) {
                templateStrings[0] += `<span id="singleNightRest" class="blue">✓</span>`;
            }

            for (let i = 0; i < unfulfilledOneNightRest; i++) {
                templateStrings[0] += `✓`;
            }

            let fulfilledTwoNightRest = ruleResult.ewdAvailability[5].consecutiveNightRestBreakCount || 0;
            let unfulfilledTwoNightRest = twoNightRestChecks - fulfilledTwoNightRest;
            for (let i = 0; i < fulfilledTwoNightRest; i++) {
                templateStrings[1] += `<span id="consecutiveNightRest" class="blue">✓</span>`;
            }

            for (let i = 0; i < unfulfilledTwoNightRest; i++) {
                templateStrings[1] += `✓`;
            }
        }

        return templateStrings;
    }

    private mapAvailabilityDisplayInfoToHtmlTableRow(availabilityDisplayInfos: IAvailabilityDisplayInfo[]): string[] {
        return availabilityDisplayInfos.map(info => {
            const svg = `<svg class="svgIcon big icon" aria-hidden="true" focusable="false"><use xlink:href="${ info.icon.href }"></use></svg>`;
            const label = info.skipTranslateLabel ? `<span class="noTranslate">${ info.label }</span>` : info.label;
            const tooltipText = this.tooltips[info.name];
            const tooltipIcon = tooltipText && !info.value.includes("<")
                ? ` <button type="button" id="${ info.htmlId }-toolTip" class="availability-toolTip" data-tooltip="${ tooltipText }"`
                    + ` aria-label="${ info.label }" aria-haspopup="dialog">`
                    + `<svg class="svgIcon" aria-hidden="true" focusable="false"><use xlink:href="#drive-info-circled"></use></svg></button>`
                : "";
            const value = info.value === DISPLAY_CHECKMARK
                ? `<td><span><svg class="svgIcon big" aria-hidden="true" focusable="false" fill="#419253"><use xlink:href='#drive-check'></use></svg></span></td>`
                : `<td class="noTranslate">${ info.value }</td>`;
            return `
            <tr id="${ info.htmlId }">
                <th><div class="th-title">${ svg } ${ label }${ tooltipIcon }</div></th>
                ${ value }
            </tr>
        `;
        });
    }
}